import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import type { AppDispatch, RootState } from "@/store/store";
import { fetchDashboardRequest } from "@/store/slices/dashboard.slice";
import { ArrowRight, Globe, RefreshCw, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

import Statistics from "@/components/dashboard/statistics";
import DashboardInsights from "@/components/dashboard/dashboardInsights";
import EarningReports from "@/components/dashboard/earningReports";
import Transactions from "@/components/dashboard/transactions";
import ActiveOrders from "@/components/dashboard/activeOrders";
import TopProducts from "@/components/dashboard/topProducts";
import { isStoreKycApprovedForPublishing } from "@/utils/storeKyc";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();
  const dispatch = useDispatch<AppDispatch>();
  const { stats, loading, error } = useSelector(
    (state: RootState) => state.dashboard,
  );
  const currentStoreInfo = useSelector(
    (state: RootState) => state.auth.currentStoreInfo,
  );

  const isKycApproved = isStoreKycApprovedForPublishing(currentStoreInfo);

  useEffect(() => {
    const hasNotFoundError = error?.status === 404;
    // Only fetch if we don't have dashboard data
    if (stats === null && !loading && !hasNotFoundError) {
      dispatch(fetchDashboardRequest());
    }
  }, [dispatch, stats, loading, error?.status]);

  const handleRefresh = () => {
    dispatch(fetchDashboardRequest());
  };

  return (
    <div className="min-h-screen bg-background px-4 py-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Header with Refresh Button */}
        <div className="relative mb-4 overflow-hidden rounded-2xl border border-border/50 bg-card/80 p-4 shadow-theme-sm backdrop-blur-xl dark:border-auth-border/60 dark:bg-auth-card/70">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.12),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(168,85,247,0.10),transparent_30%)] dark:bg-[radial-gradient(circle_at_top_left,rgba(34,197,94,0.10),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(14,165,233,0.10),transparent_30%)]" />
          <div className="relative flex items-center justify-between gap-4">
            <div>
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/15 bg-primary/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-primary dark:border-primary/20 dark:bg-primary/15">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.7)]" />
                Store pulse
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                Dashboard
              </h1>
              <p className="text-sm text-muted-foreground">
                Welcome back! Here's your store overview.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 rounded-full border-border/60 bg-background/70 shadow-sm backdrop-blur dark:border-border/50 dark:bg-card/70"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          </div>
        </div>

        {!isKycApproved && (
          <div className="mb-4 overflow-hidden rounded-2xl border border-amber-200/60 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 p-4 shadow-sm backdrop-blur-xl dark:border-amber-700/40 dark:from-amber-950/25 dark:via-orange-950/20 dark:to-rose-950/20">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-100 to-orange-100 shadow-sm dark:from-amber-800/40 dark:to-orange-800/30">
                  <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-300" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-amber-900 dark:text-amber-100">
                    Complete KYC to launch your website
                  </h2>
                  <p className="mt-1 text-sm text-amber-800/90 dark:text-amber-200/90">
                    Website launch and Web Builder access require approved KYC.
                    Complete verification to publish and go live.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => {
                  if (!storeType || !storeId) return;
                  navigate(
                    `/${storeType}/${storeId}/settings?tab=business&kycRequired=webbuilder`,
                  );
                }}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-600 via-orange-600 to-rose-600 text-white shadow-sm hover:from-amber-700 hover:via-orange-700 hover:to-rose-700"
              >
                <Globe className="h-4 w-4" />
                Go to KYC
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Statistics Row - Full Width */}
        <div className="mb-4">
          <Statistics />
        </div>

        <div className="mb-4">
          <DashboardInsights />
        </div>

        {/* Dashboard Content - Grid Layout */}
        <div className="grid grid-cols-1 gap-4">
          {/* Row 1: Earning Reports + Transactions */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="w-full">
              <EarningReports />
            </div>
            <div className="w-full">
              <Transactions />
            </div>
          </div>

          {/* Row 2: Active Orders - Full Width */}
          <div className="w-full">
            <ActiveOrders />
          </div>

          {/* Row 3: Top Products - Full Width */}
          <div className="w-full">
            <TopProducts />
          </div>

          {/* Row 3: Analytics Cards - Full Width */}
          {/* <div className="w-full">
          <AnalyticsCards />
        </div> */}

          {/* Row 4: Customer List - Full Width */}
          {/* <div className="w-full">
          <CustomerList />
        </div> */}
        </div>
      </div>
    </div>
  );
}
